'use client'
import { useCallback, useEffect, useState, type ComponentType } from 'react'

import { useIntentPreload } from './use-intent-preload'

// Keep the resolved component ready to render, not just its downloaded module.
// A first React.lazy render still suspends even when import() is already warm.
// `load` must be stable and declared at module scope.
export const useMenuComponent = <Props extends object>(
  enabled: boolean,
  requested: boolean,
  load: () => Promise<ComponentType<Props>>
) => {
  const [Component, setComponent] = useState<ComponentType<Props> | null>(null)
  const [loadError, setLoadError] = useState<Error | null>(null)
  const prepare = useCallback(async () => {
    const component = await load()
    setComponent(() => component)
  }, [load])

  useIntentPreload(enabled, prepare)

  useEffect(() => {
    if (enabled && requested && !Component) {
      // Speculative failures are ignored by useIntentPreload; an actual open
      // retries the import and forwards failures to the route error boundary.
      void prepare().catch(setLoadError)
    }
  }, [enabled, requested, Component, prepare])

  if (enabled && requested && loadError && !Component) {
    throw loadError
  }

  return Component
}
